// ABRIR LA FICHA DE UN CLIENTE DESDE OTRA PANTALLA, Y QUE "ATRÁS" DEVUELVA
// A ESA PANTALLA. (E-316, regla de oro 207.)
//
// Lissette, 2026-09-22: *«entro a cliente y cuando le doy a back me manda a
// otra pagina y no a la que estaba»*.
//
// ── LO QUE PASABA ──────────────────────────────────────────────────────────
//
// Había DOS maneras de abrir una ficha, con dos comportamientos distintos:
//
//   · Desde la LISTA DE CLIENTES (`openDetail`): empuja `#client/<id>` al
//     historial. El botón Atrás retrocede una entrada y vuelve a la lista.
//     Correcto.
//
//   · Desde CUALQUIER OTRA PANTALLA —Prospecto y Referido, Renovación,
//     Pólizas, Tareas, Reportes, Comisiones, Citas, Alertas, Enviados…—:
//     se hacía `setView("clients")`, que deja la dirección en `#clients`, y
//     sólo DESPUÉS se abría la ficha, sin volver a tocarla. Como el botón
//     Atrás sólo retrocede en el historial cuando la dirección empieza por
//     `#client/`, desde ahí no retrocedía nada: se limitaba a cerrar la ficha
//     y dejarla en la LISTA DE CLIENTES. Que no es de donde se venía.
//
// Y el Atrás DEL NAVEGADOR hacía justo lo contrario: como la ficha nunca entró
// en el historial, volvía a la pantalla de origen con la ficha abierta encima.
// Dos botones que se llaman igual y hacen cosas distintas.
//
// ── EL ARREGLO ─────────────────────────────────────────────────────────────
//
// La dirección va DIRECTA de la pantalla actual a `#client/<id>`, sin la
// parada intermedia en `#clients`. El historial queda
//
//     [ … , #onboarding , #client/<id> ]
//
// y un solo paso atrás devuelve a Prospecto y Referido. El efecto que
// sincroniza la vista con la dirección ya respeta un `#client/…` mientras la
// vista sea "clients" (`if (cur.startsWith("client/") && view === "clients")
// return`), así que no vuelve a empujar `#clients` por debajo.
//
// Vive aquí y no dentro de `deskte-full.jsx` porque son QUINCE pantallas las
// que abren una ficha, y la copia pegada quince veces es exactamente cómo
// vuelve el fallo: la número dieciséis se escribe a mano y se queda con el
// comportamiento viejo. Además, desde un `.jsx` no se puede importar en una
// prueba (regla de oro 48) — y esto hay que poder comprobarlo.

/**
 * Lleva a la ficha de un cliente desde cualquier pantalla.
 *
 * @param {object|string} cliente  La ficha (o sólo su id) que hay que abrir.
 * @param {function} setView       El `setView` de la aplicación.
 * @param {object} [ventana]       Sólo para las pruebas; por defecto `window`.
 * @returns {boolean}              `false` si no había a quién abrir.
 */
export function irAlCliente(cliente, setView, ventana) {
  const win = ventana || (typeof window !== 'undefined' ? window : null);
  const id = typeof cliente === 'string' ? cliente : (cliente && cliente.id);
  if (!id || !win) return false;
  const ficha = typeof cliente === 'string' ? { id } : cliente;

  // La entrada del historial, ANTES de cambiar de vista. Si se hiciera al
  // revés, `setView` empujaría `#clients` y volveríamos a tener la parada
  // intermedia que provocó todo esto.
  //
  // Se empuja sólo si no estábamos ya en esta misma ficha: abrir dos veces el
  // mismo cliente no debe meter dos entradas, o haría falta pulsar Atrás dos
  // veces para salir de una sola ficha.
  try {
    const destino = '#client/' + id;
    if (String((win.location && win.location.hash) || '') !== destino) {
      win.history.pushState(null, '', destino);
    }
  } catch { /* sin historial (navegador raro): la ficha se abre igual */ }

  win.__magGoToClient = ficha;
  if (typeof setView === 'function') setView('clients');

  // Para cuando YA estábamos en Clientes: la vista no se vuelve a montar, su
  // comprobación de arranque no corre otra vez, y sin esto el clic "no hace
  // nada" (Lissette 2026-07-03).
  try { win.dispatchEvent(new CustomEvent('deskte:go-to-client')); } catch { /* jsdom viejo */ }
  return true;
}
